import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { POLL_RELAYS } from '@/lib/constants';

export interface PollVoteResult {
  /** Sats per option index */
  satsByOption: Record<string, number>;
  /** Vote count per option index */
  countByOption: Record<string, number>;
  /** Total sats across all options */
  totalSats: number;
  /** Total number of votes */
  totalVotes: number;
}

/**
 * Extract the poll_option index from a zap receipt.
 * The option is encoded in the zap request (stored in the description tag).
 */
function extractPollOption(zapReceipt: NostrEvent): string | null {
  const descriptionTag = zapReceipt.tags.find(([name]) => name === 'description')?.[1];
  if (!descriptionTag) return null;

  try {
    const zapRequest = JSON.parse(descriptionTag);

    // Check for poll_option tag in the zap request
    if (zapRequest.tags) {
      for (const tag of zapRequest.tags) {
        if (tag[0] === 'poll_option' && tag[1] !== undefined) {
          return tag[1];
        }
      }
    }

    // Some clients put the option in the content as a number
    if (zapRequest.content && /^\d+$/.test(zapRequest.content.trim())) {
      return zapRequest.content.trim();
    }
  } catch {
    // Invalid JSON in description
  }

  return null;
}

/**
 * Extract sats amount from a zap receipt.
 */
function extractSats(zapReceipt: NostrEvent): number {
  // Method 1: amount tag (in millisats)
  const amountTag = zapReceipt.tags.find(([name]) => name === 'amount')?.[1];
  if (amountTag) {
    return Math.floor(parseInt(amountTag, 10) / 1000);
  }

  // Method 2: From the zap request in description
  const descriptionTag = zapReceipt.tags.find(([name]) => name === 'description')?.[1];
  if (descriptionTag) {
    try {
      const zapRequest = JSON.parse(descriptionTag);
      if (zapRequest.tags) {
        const reqAmountTag = zapRequest.tags.find(([name]: string[]) => name === 'amount')?.[1];
        if (reqAmountTag) {
          return Math.floor(parseInt(reqAmountTag, 10) / 1000);
        }
      }
    } catch {
      // Invalid JSON
    }
  }

  return 0;
}

/**
 * Fetch and tally zap votes for a kind 6969 poll.
 * Votes are kind 9735 zap receipts that reference the poll event.
 */
export function usePollVotes(pollEventId: string | undefined) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['maxi-madness', 'votes', pollEventId ?? ''],
    queryFn: async (): Promise<PollVoteResult> => {
      if (!pollEventId) {
        return { satsByOption: {}, countByOption: {}, totalSats: 0, totalVotes: 0 };
      }

      const allZaps: NostrEvent[] = [];

      // Query zap receipts from our relay group
      try {
        const relayGroup = nostr.group(POLL_RELAYS);
        const zaps = await relayGroup.query(
          [{
            kinds: [9735],
            '#e': [pollEventId],
            limit: 1000,
          }],
          { signal: AbortSignal.timeout(10000) },
        );
        allZaps.push(...zaps);
      } catch {
        // Group query failed
      }

      // Also query default pool
      try {
        const poolZaps = await nostr.query(
          [{
            kinds: [9735],
            '#e': [pollEventId],
            limit: 1000,
          }],
          { signal: AbortSignal.timeout(8000) },
        );
        allZaps.push(...poolZaps);
      } catch {
        // Pool query failed
      }

      // Deduplicate
      const seen = new Set<string>();
      const uniqueZaps: NostrEvent[] = [];
      for (const zap of allZaps) {
        if (!seen.has(zap.id)) {
          seen.add(zap.id);
          uniqueZaps.push(zap);
        }
      }

      // Tally votes
      const satsByOption: Record<string, number> = {};
      const countByOption: Record<string, number> = {};
      let totalSats = 0;
      let totalVotes = 0;

      for (const zap of uniqueZaps) {
        const optionIndex = extractPollOption(zap);
        if (optionIndex === null) continue;

        const sats = extractSats(zap);

        satsByOption[optionIndex] = (satsByOption[optionIndex] || 0) + sats;
        countByOption[optionIndex] = (countByOption[optionIndex] || 0) + 1;
        totalSats += sats;
        totalVotes++;
      }

      return { satsByOption, countByOption, totalSats, totalVotes };
    },
    enabled: !!pollEventId,
    staleTime: 20_000,
    retry: 2,
  });
}
